import { Controller } from "@hotwired/stimulus"

// Attached by shared/components/input to every field wrapper. Today it only checks
// <input type="email"> controls; any other control is left alone.
//
// Feedback rules:
// - Nothing is shown while the user types into an untouched field.
// - Leaving a non-blank, malformed email shows an inline error, sets aria-invalid,
//   and adds the error to aria-describedby.
// - Once an error is showing, it re-checks on every keystroke and clears as soon
//   as the address is well formed.
// - A server-rendered error for the field describes the submitted value, so it is
//   hidden once the user edits that value (the client check takes over).
//
// The pattern matches Ruby's URI::MailTo::EMAIL_REGEXP, which Util::Email.valid_format?
// uses on the server, so the browser and the server agree on what is valid.
export const EMAIL_PATTERN = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/
export const EMAIL_MESSAGE = "Enter a valid email address, like name@example.com."

export function validEmail(value) {
  return EMAIL_PATTERN.test(value)
}

export default class extends Controller {
  // focusout: show an error for a malformed address the user just left.
  validate(event) {
    const input = this.emailInput(event.target)
    if (!input) return

    input.dataset.inputValidationTouched = "true"
    this.check(input)
  }

  // input: live re-check, but only after the field has been left once.
  revalidate(event) {
    const input = this.emailInput(event.target)
    if (!input) return

    this.hideServerError(input)
    if (input.dataset.inputValidationTouched === "true") this.check(input)
  }

  check(input) {
    const value = input.value.trim()
    // Blank is the `required` attribute's job; don't add a format error on top.
    if (value === "" || validEmail(value)) {
      this.clearError(input)
    } else {
      this.showError(input)
    }
  }

  showError(input) {
    let message = this.messageFor(input)
    if (!message) {
      message = input.ownerDocument.createElement("p")
      message.id = this.messageId(input)
      message.className = "mt-1 text-sm text-alert-red-600"
      message.dataset.inputValidationMessage = ""
      message.textContent = EMAIL_MESSAGE
      this.element.appendChild(message)
    }
    input.setCustomValidity(EMAIL_MESSAGE)
    input.setAttribute("aria-invalid", "true")
    this.addDescribedBy(input, message.id)
  }

  clearError(input) {
    const message = this.messageFor(input)
    if (message) {
      this.removeDescribedBy(input, message.id)
      message.remove()
    }
    input.setCustomValidity("")
    if (!this.visibleServerError(input)) input.setAttribute("aria-invalid", "false")
  }

  hideServerError(input) {
    const serverError = this.visibleServerError(input)
    if (!serverError) return

    serverError.remove()
    this.removeDescribedBy(input, serverError.id)
    input.setAttribute("aria-invalid", "false")
  }

  visibleServerError(input) {
    if (!input.id) return null
    const error = this.element.querySelector(`[id="${CSS.escape(input.id)}-error"]`)
    return error && !("inputValidationMessage" in error.dataset) ? error : null
  }

  emailInput(target) {
    if (!(target instanceof target.ownerDocument.defaultView.HTMLInputElement)) return null
    // A `multiple` email field takes a comma-separated list; this single-address check doesn't apply.
    if (target.type !== "email" || target.multiple || target.disabled || target.readOnly) return null
    return this.element.contains(target) ? target : null
  }

  // Never invents an id; a field without one has no message yet.
  messageFor(input) {
    if (!input.id) return null
    return this.element.querySelector(`[id="${CSS.escape(this.messageId(input))}"]`)
  }

  // Only showError calls this before the message exists, so an id-less input gets one just then.
  messageId(input) {
    if (!input.id) input.id = `email-${Math.random().toString(36).slice(2, 10)}`
    return `${input.id}-format-error`
  }

  addDescribedBy(input, id) {
    const ids = this.describedBy(input)
    if (!ids.includes(id)) ids.push(id)
    input.setAttribute("aria-describedby", ids.join(" "))
  }

  removeDescribedBy(input, id) {
    const ids = this.describedBy(input).filter((existing) => existing !== id)
    if (ids.length) {
      input.setAttribute("aria-describedby", ids.join(" "))
    } else {
      input.removeAttribute("aria-describedby")
    }
  }

  describedBy(input) {
    return (input.getAttribute("aria-describedby") || "").split(/\s+/).filter(Boolean)
  }
}
